import { transformAddress } from "../../ecommerce/cart/helpers/cartApiHelpers/cartDataTransformationHelpers/transformAddress";
import type { RawUserProfile } from "../../../types/api/customRestApi/customRestApiEndpointsTypes";
import type { UserProfile } from "../types/userAuthApiTypes";
import type { TransformedAddress } from "../../ecommerce/cart/types/cartApiTypes";

/**
 * Transforms the raw user profile response (snake_case) to the UI model (camelCase).
 */
export const transformUserProfile = (raw: RawUserProfile): UserProfile => {
  // 1. Prepare extra billing fields for the transformer
  // These come from the dedicated 'additional_billing_info' object in the response
  const billingExtras = {
    customerType: raw.additional_billing_info?.customer_type || "individual",
    tckn: raw.additional_billing_info?.tckn || "",
    vkn: raw.additional_billing_info?.vkn || "",
  };

  // 2. Transform Addresses using the shared helper
  // If the address object is missing or empty (e.g. new user), return an empty object cast as TransformedAddress
  const billingAddress = raw.billing_address
    ? transformAddress(raw.billing_address, billingExtras)
    : ({} as TransformedAddress);

  const shippingAddress = raw.shipping_address
    ? transformAddress(raw.shipping_address)
    : ({} as TransformedAddress);

  return {
    id: raw.id,
    firstName: raw.first_name,
    lastName: raw.last_name,
    email: raw.email,
    displayName: raw.display_name,
    billingAddress,
    shippingAddress,
    // Map snake_case response to camelCase UI model for Orders
    orders: (raw.orders || []).map((order) => ({
      id: order.id,
      orderNumber: order.order_number,
      orderKey: order.order_key,
      status: order.status,
      date: order.date_created,
      total: order.total,
      itemCount: order.item_count,
      currency: order.currency,
    })),
    // Map snake_case response to camelCase UI model for Coupons
    coupons: (raw.coupons || []).map((c) => ({
      code: c.code,
      description: c.description,
      discountType: c.discount_type,
      amount: c.amount,
      isUsed: c.is_used,
      createdAt: c.created_at,
      usedAt: c.used_at,
    })),
  };
};
